import React, { useEffect, useMemo, useRef, useState } from 'react';
import worldMapData from './worldMapData.json';

/**
 * Animated dotted world map with live trade routes — the cinematic backdrop
 * for directory pages.
 *
 * Depth layers (parallax factors applied on scroll):
 *   depth-0  dotted landmass          0.10x  (far background)
 *   depth-1  glow blobs               0.25x
 *   depth-2  trade-route arcs         0.50x
 *   depth-3  hub nodes + home marker  0.80x
 *
 * Routes are drawn as quadratic arcs between real country centroids and
 * animated with a travelling dash "comet". A `homeCountry` prop pulses that
 * country's node and re-roots every arc there, so each company page shows its
 * own trade lanes.
 *
 * All motion is GPU-safe (transform / opacity / stroke-dashoffset) and is
 * disabled entirely under prefers-reduced-motion.
 */

const VIEW_W = 1000;
const VIEW_H = 440;

// Alias table for the country strings that appear in directory listings but
// don't match the map dataset's naming.
const COUNTRY_ALIASES = {
  usa: 'united states of america',
  us: 'united states of america',
  'u.s.a.': 'united states of america',
  'united states': 'united states of america',
  uk: 'united kingdom',
  uae: 'united arab emirates',
  'south korea': 'south korea',
  'republic of korea': 'south korea',
  russia: 'russia',
  'ivory coast': "côte d'ivoire",
  'czech republic': 'czechia',
  vietnam: 'vietnam',
};

// Major commodity trading hubs — the default network when a page has no country.
const DEFAULT_HUBS = [
  'brazil', 'united states of america', 'netherlands', 'china', 'india',
  'united arab emirates', 'singapore', 'south africa', 'australia', 'argentina',
  'thailand', 'united kingdom', 'turkey', 'egypt', 'mexico', 'indonesia',
];

export const centroidFor = (country) => {
  if (!country) return null;
  const key = String(country).trim().toLowerCase();
  const resolved = COUNTRY_ALIASES[key] || key;
  return worldMapData.centroids[resolved] || null;
};

/** Quadratic arc between two points, bowed away from the equator line. */
const arcPath = ([x1, y1], [x2, y2]) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dist = Math.hypot(x2 - x1, y2 - y1);
  const lift = Math.min(150, dist * 0.32);
  return `M ${x1} ${y1} Q ${mx} ${my - lift} ${x2} ${y2}`;
};

const TradeRoutesBackground = ({
  homeCountry = null,
  className = '',
  intensity = 1,       // 0-1, scales dot/arc opacity
  parallax = true,
}) => {
  const wrapRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Scroll parallax — rAF-throttled, transform only
  useEffect(() => {
    if (!parallax || reduced) return;
    const el = wrapRef.current;
    if (!el) return;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    if (coarse) return; // touch devices: skip parallax work entirely

    let raf = null;
    const apply = () => {
      raf = null;
      const y = window.scrollY || 0;
      el.querySelectorAll('[data-depth]').forEach((layer) => {
        const factor = parseFloat(layer.dataset.depth) || 0;
        layer.style.transform = `translate3d(0, ${(y * factor).toFixed(2)}px, 0)`;
      });
    };
    const onScroll = () => { if (raf === null) raf = requestAnimationFrame(apply); };
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [parallax, reduced]);

  const home = useMemo(() => centroidFor(homeCountry), [homeCountry]);

  // Build the route set: every arc starts at the company's country when known,
  // otherwise the hubs are chained into a global mesh.
  const routes = useMemo(() => {
    const hubs = DEFAULT_HUBS
      .map((n) => ({ name: n, pt: worldMapData.centroids[n] }))
      .filter((h) => h.pt);

    if (home) {
      return hubs
        .filter((h) => Math.hypot(h.pt[0] - home[0], h.pt[1] - home[1]) > 60)
        .slice(0, 9)
        .map((h, i) => ({ d: arcPath(home, h.pt), delay: i * 0.9, end: h.pt }));
    }
    const pairs = [];
    for (let i = 0; i < hubs.length - 1; i += 2) {
      pairs.push({
        d: arcPath(hubs[i].pt, hubs[i + 1].pt),
        delay: (i / 2) * 0.9,
        end: hubs[i + 1].pt,
      });
    }
    return pairs;
  }, [home]);

  const hubPoints = useMemo(
    () => DEFAULT_HUBS.map((n) => worldMapData.centroids[n]).filter(Boolean),
    [],
  );

  return (
    <div
      ref={wrapRef}
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
    >
      {/* depth-1 — atmospheric glow blobs */}
      <div data-depth="0.25" className="absolute inset-0">
        <div
          className="absolute rounded-full"
          style={{
            width: '46rem', height: '46rem', left: '-12rem', top: '-18rem',
            background: 'radial-gradient(circle, rgba(56,132,255,0.28) 0%, rgba(56,132,255,0) 68%)',
            filter: 'blur(6px)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '38rem', height: '38rem', right: '-10rem', bottom: '-16rem',
            background: 'radial-gradient(circle, rgba(245,138,90,0.22) 0%, rgba(245,138,90,0) 70%)',
            filter: 'blur(6px)',
          }}
        />
      </div>

      {/* depth-0 / 2 / 3 — the map itself */}
      <svg
        viewBox={`20 40 ${VIEW_W - 20} ${VIEW_H - 60}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.95 }}
      >
        <defs>
          <linearGradient id="tr-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#F58A5A" stopOpacity="0.05" />
            <stop offset="50%" stopColor="#FFD166" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#F58A5A" stopOpacity="0.05" />
          </linearGradient>
          <radialGradient id="tr-node">
            <stop offset="0%" stopColor="#FFD166" stopOpacity="1" />
            <stop offset="100%" stopColor="#FFD166" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Dotted landmass */}
        <g data-depth="0.10">
          {worldMapData.dots.map(([x, y], i) => (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="1.6"
              fill="#ffffff"
              opacity={0.22 * intensity}
            />
          ))}
        </g>

        {/* Trade-route arcs */}
        <g data-depth="0.5">
          {routes.map((r, i) => (
            <g key={i}>
              <path
                d={r.d}
                fill="none"
                stroke="#ffffff"
                strokeOpacity={0.1 * intensity}
                strokeWidth="1"
                strokeDasharray="4 6"
              />
              {!reduced && (
                <path
                  className="tr-comet"
                  d={r.d}
                  fill="none"
                  stroke="url(#tr-arc)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{ animationDelay: `${r.delay}s` }}
                />
              )}
            </g>
          ))}
        </g>

        {/* Hub nodes */}
        <g data-depth="0.8">
          {hubPoints.map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="2.4" fill="#FFD166" opacity="0.75" />
              {!reduced && (
                <circle
                  className="tr-pulse"
                  cx={x}
                  cy={y}
                  r="2.4"
                  fill="none"
                  stroke="#FFD166"
                  strokeWidth="1"
                  style={{ animationDelay: `${(i % 8) * 0.55}s` }}
                />
              )}
            </g>
          ))}

          {/* The company's own country — the anchor of every arc */}
          {home && (
            <g>
              <circle cx={home[0]} cy={home[1]} r="26" fill="url(#tr-node)" opacity="0.5" />
              <circle cx={home[0]} cy={home[1]} r="4.5" fill="#ffffff" />
              {!reduced && (
                <>
                  <circle
                    className="tr-pulse tr-pulse-lg"
                    cx={home[0]}
                    cy={home[1]}
                    r="5"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="1.5"
                  />
                  <circle
                    className="tr-pulse tr-pulse-lg"
                    cx={home[0]}
                    cy={home[1]}
                    r="5"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="1.5"
                    style={{ animationDelay: '1.4s' }}
                  />
                </>
              )}
            </g>
          )}
        </g>
      </svg>

      {/* Vignette so foreground text always keeps contrast */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 50% 0%, rgba(15,23,42,0) 35%, rgba(15,23,42,0.55) 100%)',
        }}
      />
    </div>
  );
};

export default TradeRoutesBackground;
